const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Token storage service
export class TokenService {
  private static readonly TOKEN_KEY = 'recyda_auth_token';
  private static readonly TOKEN_EXPIRY_KEY = 'recyda_token_expiry';

  // Save token to localStorage
  static saveToken(token: string, expiresIn?: number): void {
    try {
      localStorage.setItem(this.TOKEN_KEY, token);
      
      if (expiresIn) {
        const expiryTime = Date.now() + (expiresIn * 1000);
        localStorage.setItem(this.TOKEN_EXPIRY_KEY, expiryTime.toString());
      }
      
      console.log('✅ Token saved to localStorage');
    } catch (error) {
      console.error('❌ Error saving token:', error);
    }
  }

  // Get token from localStorage
  static getToken(): string | null {
    try {
      const token = localStorage.getItem(this.TOKEN_KEY);
      const expiryTime = localStorage.getItem(this.TOKEN_EXPIRY_KEY);
      
      if (!token) {
        console.log('🔍 No token found in localStorage');
        return null;
      }
      
      // Check if token is expired
      if (expiryTime) {
        const expiry = parseInt(expiryTime);
        if (Date.now() > expiry) {
          console.log('⏰ Token expired, removing from localStorage');
          this.clearToken();
          return null;
        }
      }
      
      console.log('✅ Token retrieved from localStorage');
      return token;
    } catch (error) {
      console.error('❌ Error retrieving token:', error);
      return null;
    }
  }

  // Check if token exists and is valid
  static hasValidToken(): boolean {
    const token = this.getToken();
    return token !== null && token.length > 0;
  }

  // Clear token from localStorage
  static clearToken(): void {
    try {
      localStorage.removeItem(this.TOKEN_KEY);
      localStorage.removeItem(this.TOKEN_EXPIRY_KEY);
      console.log('🗑️ Token cleared from localStorage');
    } catch (error) {
      console.error('❌ Error clearing token:', error);
    }
  }

  // Get token with Bearer prefix
  private static getBearerToken(): string | null {
    const token = this.getToken();
    return token ? `Bearer ${token}` : null;
  }

  // Check token expiry
  static isTokenExpired(): boolean {
    try {
      const expiryTime = localStorage.getItem(this.TOKEN_EXPIRY_KEY);
      if (!expiryTime) return false;
      
      const expiry = parseInt(expiryTime);
      return Date.now() > expiry;
    } catch (error) {
      console.error('❌ Error checking token expiry:', error);
      return false;
    }
  }
}

// Helper function to make API calls to backend with automatic token injection
export const makeBackendRequest = async (endpoint: string, options: RequestInit = {}) => {
  const url = `${BACKEND_BASE_URL}${endpoint}`;
  
  console.log('🔍 Making request to:', url);
  
  // Get cached token
  const token = TokenService.getToken();
  
  // Ensure headers are properly encoded and contain only ASCII characters
  const defaultHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Add Authorization header if token exists
  if (token && TokenService.hasValidToken()) {
    const cleanToken = token.trim();
    // eslint-disable-next-line no-control-regex
    if (/^[\x00-\x7F]*$/.test(cleanToken)) {
      defaultHeaders['Authorization'] = `Bearer ${cleanToken}`;
      console.log('🔑 Adding cached token to request');
    } else {
      console.warn('⚠️ Token contains non-ASCII characters, skipping');
    }
  } else {
    console.log('🔑 No valid token found in cache');
  }

  // Safely merge headers, ensuring they are strings
  if (options.headers) {
    Object.entries(options.headers).forEach(([key, value]) => {
      if (typeof value === 'string') {
        // Ensure the header value is ASCII-safe
        defaultHeaders[key] = value;
      }
    });
  }

  const response = await fetch(url, {
    ...options,
    headers: defaultHeaders,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
};

export const makeBackendBlobRequest = async (endpoint: string, options: RequestInit = {}) => {
  const url = `${BACKEND_BASE_URL}${endpoint}`;
  
  console.log('🔍 Making blob request to:', url);
  
  // Get cached token
  const token = TokenService.getToken();
  
  // Ensure headers are properly encoded and contain only ASCII characters
  const defaultHeaders: Record<string, string> = {};

  // Add Authorization header if token exists
  if (token && TokenService.hasValidToken()) {
    const cleanToken = token.trim();
    // eslint-disable-next-line no-control-regex
    if (/^[\x00-\x7F]*$/.test(cleanToken)) {
      defaultHeaders['Authorization'] = `Bearer ${cleanToken}`;
      console.log('🔑 Adding cached token to blob request');
    } else {
      console.warn('⚠️ Token contains non-ASCII characters, skipping');
    }
  } else {
    console.log('🔑 No valid token found in cache');
  }

  // Safely merge headers, ensuring they are strings
  if (options.headers) {
    Object.entries(options.headers).forEach(([key, value]) => {
      if (typeof value === 'string') {
        // Ensure the header value is ASCII-safe
        defaultHeaders[key] = value;
      }
    });
  }

  const response = await fetch(url, {
    ...options,
    headers: defaultHeaders,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
  }

  return response;
};
